import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAppStore } from '@/store/app';
import { useAuthStore } from '@/store/auth';
import { api, type Pin, type Reporte, type Ajuste } from '@/lib/api';
import MapaElParaiso from '@/components/map/MapaElParaiso';

const ESTADO_COLOR: Record<string, string> = {
  pendiente: '#ca8a04', proceso: '#2563eb', resuelto: '#22c55e',
};
const ESTADO_LABEL: Record<string, string> = {
  pendiente: 'Pendiente', proceso: 'En proceso', resuelto: 'Resuelto',
};

export default function Panel() {
  const { catActiva, openPinId, isDark, abrirPopup, cerrarPopup } = useAppStore();
  const { token, institucion } = useAuthStore();

  const { data: pins = [] } = useQuery<Pin[]>({
    queryKey: ['pins', institucion?.id],
    queryFn: () => api.pins(token!, institucion?.id !== 'todos' ? institucion?.id : undefined),
    enabled: !!token,
  });

  const { data: reportes = [] } = useQuery<Reporte[]>({
    queryKey: ['reportes', institucion?.id],
    queryFn: () => api.reportes(token!, institucion?.id !== 'todos' ? { institucionId: institucion!.id } : undefined),
    enabled: !!token,
  });

  const openPin = pins.find(p => p.id === openPinId);
  const openReporte = openPin
    ? (reportes.find(r => r.titulo === openPin.label && r.categoria.slug === openPin.categoria.slug)
       ?? reportes.find(r => r.titulo === openPin.label)
       ?? null)
    : null;

  const { data: ajustes } = useQuery<Ajuste>({
    queryKey: ['ajustes'],
    queryFn: () => api.ajustes(token!),
    enabled: !!token,
  });

  const pendientes = reportes.filter(r => r.estado === 'pendiente').length;
  const enProceso = reportes.filter(r => r.estado === 'proceso').length;
  const resueltos = reportes.filter(r => r.estado === 'resuelto').length;

  return (
    <div style={{ position: 'absolute', inset: 0 }}>
      <MapaElParaiso pins={pins} catActiva={catActiva} onPinClick={abrirPopup} show3D={ajustes?.edif3d ?? true} instSlug={institucion?.slug ?? null} nocturno={ajustes?.nocturno ?? false} />

      {/* Stats overlay */}
      <div style={{ position: 'absolute', bottom: 90, right: 16, zIndex: 20, display: 'flex', flexDirection: 'column', gap: 8 }}>
        {[
          { label: 'Pendientes', value: pendientes, color: '#ca8a04', icon: 'ti-clock' },
          { label: 'En proceso', value: enProceso, color: '#2563eb', icon: 'ti-loader-2' },
          { label: 'Resueltos', value: resueltos, color: '#22c55e', icon: 'ti-check' },
        ].map(s => (
          <div key={s.label} style={{ background: isDark ? 'rgba(8,16,40,0.97)' : 'rgba(10,22,40,0.84)', backdropFilter: 'blur(14px)', borderRadius: 12, padding: '9px 14px', display: 'flex', alignItems: 'center', gap: 8, minWidth: 115, border: isDark ? '1px solid rgba(100,150,255,0.2)' : '1px solid rgba(255,255,255,0.08)' }}>
            <i className={`ti ${s.icon}`} style={{ color: s.color, fontSize: 14 }} />
            <div>
              <div style={{ fontSize: 16, fontWeight: 800, color: '#fff', lineHeight: 1 }}>{s.value}</div>
              <div style={{ fontSize: 9.5, color: 'rgba(255,255,255,0.45)', marginTop: 2 }}>{s.label}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Ficha lateral */}
      {openPin && (
        <div style={{ position: 'absolute', top: 110, left: 16, bottom: 16, width: 320, zIndex: 30, display: 'flex', flexDirection: 'column', borderRadius: 20, overflow: 'hidden', boxShadow: '0 8px 32px rgba(30,64,175,0.18)', border: '1px solid rgba(147,197,253,0.4)', animation: 'slideInLeft 220ms ease-out' }}>

          {/* Cabecera con color de categoría */}
          <div style={{ background: openPin.categoria.color, padding: '16px 14px 14px', display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
            <div style={{ width: 42, height: 42, borderRadius: '50%', background: 'rgba(255,255,255,0.22)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, flexShrink: 0 }}>
              <i className={`ti ${openPin.categoria.icon}`} style={{ color: '#fff' }} />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 14, fontWeight: 800, color: '#fff', lineHeight: 1.2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{openPin.label}</div>
              <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.78)', marginTop: 3 }}>{openPin.categoria.label}</div>
            </div>
            <button onClick={cerrarPopup} style={{ color: 'rgba(255,255,255,0.75)', fontSize: 18, flexShrink: 0, lineHeight: 1, padding: 2 }}>
              <i className="ti ti-x" />
            </button>
          </div>

          {/* Cuerpo — fondo azul clarito */}
          <div style={{ flex: 1, overflowY: 'auto', background: 'rgba(219,234,254,0.97)', backdropFilter: 'blur(16px)', padding: 16 }}>

            {/* Estado + fecha */}
            {openReporte && (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
                <span style={{ fontSize: 11, fontWeight: 700, padding: '4px 10px', borderRadius: 20, background: `${ESTADO_COLOR[openReporte.estado]}22`, color: ESTADO_COLOR[openReporte.estado], border: `1px solid ${ESTADO_COLOR[openReporte.estado]}55` }}>
                  {ESTADO_LABEL[openReporte.estado]}
                </span>
                <span style={{ fontSize: 10, color: '#64748b' }}>
                  {new Date(openReporte.createdAt).toLocaleDateString('es-VE', { day: '2-digit', month: 'short', year: 'numeric' })}
                </span>
              </div>
            )}

            {/* Grid de metadatos */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 14 }}>
              {([
                { icon: 'ti-user', label: 'Brigadista', value: openPin.brigadista },
                { icon: 'ti-alert-triangle', label: 'Prioridad', value: openReporte?.prioridad },
                { icon: 'ti-map-pin', label: 'Sector', value: openReporte?.sector },
                { icon: 'ti-calendar', label: 'Fecha', value: openReporte ? new Date(openReporte.createdAt).toLocaleDateString('es-VE') : null },
              ] as { icon: string; label: string; value?: string | null }[]).filter(f => f.value).map(f => (
                <div key={f.label} style={{ background: 'rgba(255,255,255,0.72)', borderRadius: 10, padding: '8px 10px', border: '1px solid rgba(147,197,253,0.3)' }}>
                  <div style={{ fontSize: 9, color: '#94a3b8', textTransform: 'uppercase' as const, letterSpacing: 0.5, marginBottom: 3, display: 'flex', alignItems: 'center', gap: 4 }}>
                    <i className={`ti ${f.icon}`} style={{ fontSize: 10 }} />{f.label}
                  </div>
                  <div style={{ fontSize: 12, fontWeight: 700, color: '#1e40af', textTransform: 'capitalize' as const }}>{f.value}</div>
                </div>
              ))}
            </div>

            {/* Detalles del hallazgo (descripción parseada) */}
            {openReporte?.descripcion && (
              <div style={{ background: 'rgba(255,255,255,0.65)', borderRadius: 12, padding: '10px 12px', border: '1px solid rgba(147,197,253,0.3)' }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: '#1e40af', textTransform: 'uppercase' as const, letterSpacing: 0.6, marginBottom: 10 }}>
                  <i className="ti ti-clipboard-list" style={{ marginRight: 4 }} />Detalles del hallazgo
                </div>
                {openReporte.descripcion.split('\n').filter(l => l.trim()).map((line, i) => {
                  const sep = line.indexOf(':');
                  const key = sep >= 0 ? line.slice(0, sep).trim() : line.trim();
                  const val = sep >= 0 ? line.slice(sep + 1).trim() : '';
                  return (
                    <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 7, alignItems: 'flex-start' }}>
                      <div style={{ fontSize: 11, color: '#64748b', minWidth: 90, flexShrink: 0, fontWeight: 500 }}>{key}</div>
                      <div style={{ fontSize: 11, color: '#1e3a5f', fontWeight: 700, wordBreak: 'break-word' as const }}>{val || '—'}</div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Coordenadas */}
            {(openPin.lat && openPin.lng) && (
              <div style={{ marginTop: 10, background: 'rgba(255,255,255,0.55)', borderRadius: 10, padding: '8px 10px', border: '1px solid rgba(147,197,253,0.25)' }}>
                <div style={{ fontSize: 9, color: '#94a3b8', textTransform: 'uppercase' as const, letterSpacing: 0.5, marginBottom: 3 }}>
                  <i className="ti ti-crosshair" style={{ fontSize: 10, marginRight: 4 }} />Coordenadas
                </div>
                <div style={{ fontSize: 10, color: '#1e40af', fontFamily: 'monospace' }}>
                  {openPin.lat.toFixed(6)}, {openPin.lng.toFixed(6)}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
